import React, { useEffect, useMemo, useState } from "react";
import { useRoute, useNavigation } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/navigation.types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  useGetAiTutorQuery,
  useGetCompanionChatByAiTutorIdQuery,
  useGetAiTutorVideosQuery,
  useGetAllUserIlEsQuery,
} from "../graphql/generated/graphql";
import AITutorChatLayoutContainer from "../containers/chat/AITutorChatLayoutContainer/AITutorChatLayoutContainer";
import { type Objective } from "../containers/companion/LearningGoalsContainer";
import type { ExplorationMediaItem } from "../components/learning-activity/ExplorationMenu/ExplorationMenu";

const LearningActivityView = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Interactive Learning Environment">>();
  const { activityId } = route.params || {};
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { refetch: refetchUserILEsActive } = useGetAllUserIlEsQuery({
    skip: true,
    variables: { completionStatus: "active" } as never,
  });
  const { refetch: refetchUserILEsCompleted } = useGetAllUserIlEsQuery({
    skip: true,
    variables: { completionStatus: "completed" } as never,
  });
  const [completedObjectiveIds, setCompletedObjectiveIds] = useState<string[]>([]);

  const {
    data: aiTutorData,
    loading: aiTutorLoading,
    refetch: refetchAiTutor,
  } = useGetAiTutorQuery({
    variables: { id: activityId },
    skip: !activityId,
  });

  const { data: companionChatByAiTutorData, loading: companionChatByAiTutorLoading } =
    useGetCompanionChatByAiTutorIdQuery({
      variables: { aiTutorId: activityId },
      skip: !activityId,
      fetchPolicy: "network-only",
    });

  const { data: aiTutorVideosData, refetch: refetchAiTutorVideos } = useGetAiTutorVideosQuery({
    variables: { id: activityId },
    skip: !activityId,
    fetchPolicy: "network-only",
  });

  const handleExitPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("Learning Activities");
    }
  };

  const handleBackToLearningActivities = () => {
    navigation.navigate("Learning Activities");
  };

  const explorationItems = useMemo<ExplorationMediaItem[]>(() => {
    const videos = aiTutorVideosData?.getAITutorVideos?.data ?? [];
    return videos.map((video) => ({
      id: video.id,
      title: video.title,
      type: "video",
      metadata: "Suggested",
      thumbnail:
        video.thumbnailUrl || (video.videoId ? `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg` : ""),
      videoId: video.videoId,
      description: video.description ?? undefined,
      mediaUrl: video.url ?? undefined,
    }));
  }, [aiTutorVideosData]);

  const handleObjectiveCompleted = (objectiveIds: string[] = []) => {
    if (objectiveIds.length > 0) {
      setCompletedObjectiveIds((previousIds) =>
        Array.from(new Set([...previousIds, ...objectiveIds.filter((id): id is string => Boolean(id))]))
      );
    }
    if (!activityId) return;
    void Promise.all([
      refetchAiTutor({ id: activityId }),
      refetchAiTutorVideos({ id: activityId }),
      refetchUserILEsActive(),
      refetchUserILEsCompleted(),
    ]);
  };

  const objectiveData = useMemo<Objective[]>(() => {
    const objectives = [...(aiTutorData?.getAITutor?.data?.[0]?.objectives ?? [])].sort(
      (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
    );
    return objectives.reduce<Objective[]>((acc, objective) => {
      if (!objective) return acc;
      const isPersistedCompleted = objective.status?.trim().toLowerCase() === "completed";
      acc.push({
        id: objective.id,
        objective: objective.objective ?? "",
        evalCriteria: objective.evalCriteria ?? [],
        isCompleted: isPersistedCompleted || completedObjectiveIds.includes(objective.id),
      });
      return acc;
    }, []);
  }, [aiTutorData, completedObjectiveIds]);

  useEffect(() => {
    setCompletedObjectiveIds([]);
  }, [activityId]);

  useEffect(() => {
    if (!activityId) return;
    refetchAiTutorVideos();
  }, [objectiveData, refetchAiTutorVideos, activityId]);

  return (
    <AITutorChatLayoutContainer
      resourceId={activityId ?? ""}
      title={aiTutorData?.getAITutor?.data?.[0]?.title ?? "Learning Activity"}
      loading={aiTutorLoading}
      backButtonText="Back to learning activities"
      onBackPress={handleExitPress}
      onModalBackPress={handleBackToLearningActivities}
      hasNextActivity={false}
      chatData={companionChatByAiTutorData?.getCompanionChatByAiTutorId?.data}
      chatLoading={companionChatByAiTutorLoading}
      objectives={objectiveData}
      onObjectiveCompleted={handleObjectiveCompleted}
      explorationItems={explorationItems}
    />
  );
};

export default LearningActivityView;
